import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { profilesApi, sectionsApi, extolsApi, contributionsApi, galleryApi, favouritesApi, reportsApi, sendTributeApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Heart, ArrowLeft, Sparkles, Star, Loader2, MapPin, BookOpen, Clock,
  MessageCircle, GraduationCap, Palette, Shield, Users, Globe, Briefcase,
  Image, BookmarkPlus, Bookmark, Flag, Send, Mail, Calendar
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const SECTION_CONFIG = {
  story: { name: 'Their Story', icon: BookOpen },
  impact: { name: 'Their Impact', icon: Sparkles },
  moments: { name: 'Cherished Moments', icon: Clock },
  values: { name: 'What They Stand For', icon: Star },
  messages: { name: 'Words of Love', icon: MessageCircle }
};

const CATEGORY_CONFIG = {
  academic: { icon: GraduationCap, label: 'Academic' },
  arts: { icon: Palette, label: 'Arts & Culture' },
  military: { icon: Shield, label: 'Military & Service' },
  community: { icon: Users, label: 'Community' },
  global: { icon: Globe, label: 'Global' },
  professional: { icon: Briefcase, label: 'Professional' }
};

export default function TributeViewPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get('invite');
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [sections, setSections] = useState([]);
  const [extols, setExtols] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFavourited, setIsFavourited] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendData, setSendData] = useState({ recipient_email: '', recipient_name: '', personal_message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileRes, sectionsRes, extolsRes, contribRes, galleryRes] = await Promise.all([
          profilesApi.getById(id, invite),
          sectionsApi.getAll(id, invite),
          extolsApi.getAll(id, invite),
          contributionsApi.getAll(id, invite).catch(() => ({ data: [] })),
          galleryApi.getAll(id, invite).catch(() => ({ data: [] }))
        ]);
        setProfile(profileRes.data);
        setSections(sectionsRes.data);
        setExtols(extolsRes.data);
        setContributions(contribRes.data);
        setGallery(galleryRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load tribute');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, invite]);

  const handleReaction = async (extolId, type) => {
    try {
      const { data } = await extolsApi.react(id, extolId, type);
      setExtols(extols.map(e => e.id === extolId ? { ...e, reactions: data.reactions } : e));
    } catch (err) { console.error('Reaction failed:', err); }
  };

  const toggleFavourite = async () => {
    if (!user) { toast.error('Sign in to save favourites'); return; }
    try {
      const { data } = await favouritesApi.toggle(id);
      setIsFavourited(data.is_favourited);
      toast.success(data.is_favourited ? 'Saved' : 'Removed from favourites');
    } catch { toast.error('Could not update'); }
  };

  const handleReport = async () => {
    if (!reportReason) { toast.error('Please select a reason'); return; }
    try {
      await reportsApi.create(id, { reason: reportReason, details: reportDetails });
      setShowReportDialog(false);
      setReportReason('');
      setReportDetails('');
      toast.success('Report submitted. Our team will review it.');
    } catch { toast.error('Failed to submit report'); }
  };

  const handleSendTribute = async () => {
    if (!sendData.recipient_email || !sendData.recipient_name) { toast.error('Please fill in name and email'); return; }
    setSending(true);
    try {
      await sendTributeApi.send(id, sendData);
      setShowSendDialog(false);
      setSendData({ recipient_email: '', recipient_name: '', personal_message: '' });
      toast.success(`Tribute sent to ${sendData.recipient_name}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="text-center"><p className="text-[#1a1a2e]/50 text-lg mb-4">{error}</p><Link to="/"><Button className="rounded-full">Go Home</Button></Link></div>
    </div>
  );
  if (!profile) return null;

  const isLiving = profile.profile_type === 'living';
  const isOwner = user && profile.created_by === user.id;
  const groupedContribs = contributions.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-100/40 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-pink-100/40 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to={isOwner ? '/dashboard' : '/explore'} className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e]" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">{isOwner ? 'Dashboard' : 'Explore'}</span>
          </Link>
          <div className="flex items-center gap-3">
            {isOwner && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowSendDialog(true)}
                  className="border-[#1a1a2e]/10 rounded-full text-[#1a1a2e]/70" data-testid="send-tribute-button">
                  <Mail className="w-4 h-4 mr-2" />Send
                </Button>
                <Link to={`/edit/${id}`}>
                  <Button variant="outline" size="sm" className="border-[#1a1a2e]/10 rounded-full" data-testid="edit-tribute-button">Edit</Button>
                </Link>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={toggleFavourite} data-testid="fav-button">
              {isFavourited ? <Bookmark className="w-5 h-5 text-[#7c3aed] fill-[#7c3aed]" /> : <BookmarkPlus className="w-5 h-5 text-[#1a1a2e]/30" />}
            </Button>
            {!isOwner && (
              <Button variant="ghost" size="icon" onClick={() => setShowReportDialog(true)} className="text-[#1a1a2e]/30 hover:text-red-500" data-testid="report-button">
                <Flag className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Hero */}
        <div className="text-center mb-16">
          {profile.photo_url ? (
            <div className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-6 shadow-2xl border-4 border-white">
              <img src={profile.photo_url.startsWith('http') ? profile.photo_url : `${profile.photo_url}`} alt={profile.person_name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-36 h-36 rounded-full mx-auto mb-6 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center shadow-2xl border-4 border-white">
              <span className="text-5xl font-bold text-[#1a1a2e]/20">{profile.person_name.charAt(0)}</span>
            </div>
          )}
          <Badge className={`${isLiving ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-violet-50 text-violet-600 border-violet-200'} border rounded-full mb-4`}>
            {isLiving ? <Sparkles className="w-3 h-3 mr-1" /> : <Star className="w-3 h-3 mr-1" />}
            {isLiving ? 'Living Tribute' : 'Legacy Tribute'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1a2e] tracking-tight mb-2" data-testid="tribute-person-name">{profile.person_name}</h1>
          {(profile.location_city || profile.location_country) && (
            <p className="text-[#1a1a2e]/40 flex items-center justify-center gap-1 mb-3" data-testid="tribute-location">
              <MapPin className="w-4 h-4" />{[profile.location_city, profile.location_country].filter(Boolean).join(', ')}
            </p>
          )}
          {(profile.date_of_birth || profile.date_of_passing) && (
            <p className="text-[#1a1a2e]/40 flex items-center justify-center gap-1 mb-3 text-sm" data-testid="tribute-dates">
              <Calendar className="w-3.5 h-3.5" />
              {profile.date_of_birth && new Date(profile.date_of_birth).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
              {profile.date_of_birth && profile.date_of_passing && ' — '}
              {profile.date_of_passing && new Date(profile.date_of_passing).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <p className="text-lg text-[#1a1a2e]/50 mb-2">{profile.relationship}</p>
          <p className="text-xl text-[#1a1a2e]/70 italic max-w-2xl mx-auto" data-testid="tribute-headline">"{profile.headline}"</p>
          <p className="text-sm text-[#1a1a2e]/30 mt-4">Tribute by {profile.creator_name}</p>
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="mb-16">
            <h2 className="text-lg font-bold text-[#1a1a2e] mb-4 flex items-center gap-2"><Image className="w-5 h-5 text-[#7c3aed]" />Photos</h2>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="tribute-gallery">
              {gallery.map(img => (
                <div key={img.id} className="aspect-square rounded-2xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity shadow-lg"
                  onClick={() => { setSelectedImage(img); setShowGalleryModal(true); }}>
                  <img src={img.image_url.startsWith('http') ? img.image_url : `${img.image_url}`} alt={img.caption || ''} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Story Sections */}
        {sections.length > 0 && (
          <div className="space-y-8 mb-16">
            {sections.map(section => {
              const config = SECTION_CONFIG[section.section_type] || { name: section.section_type, icon: BookOpen };
              const Icon = config.icon;
              return (
                <div key={section.id} className="relative" data-testid={`section-${section.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#7c3aed]" />
                    </div>
                    <h2 className="text-xl font-bold text-[#1a1a2e]">{config.name}</h2>
                  </div>
                  <div className="pl-[52px]">
                    <p className="text-[#1a1a2e]/70 leading-relaxed whitespace-pre-wrap">{section.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contributions */}
        {contributions.length > 0 && (
          <div className="mb-16" data-testid="tribute-contributions">
            <h2 className="text-xl font-bold text-[#1a1a2e] mb-6">Contributions & Achievements</h2>
            <div className="space-y-6">
              {Object.entries(groupedContribs).map(([cat, items]) => {
                const config = CATEGORY_CONFIG[cat] || { icon: Star, label: cat };
                const Icon = config.icon;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5 text-[#7c3aed]" />
                      <h3 className="font-semibold text-[#1a1a2e]">{config.label}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 pl-7">
                      {items.map(item => (
                        <Card key={item.id} className="border-[#1a1a2e]/5 bg-white shadow-sm">
                          <CardContent className="p-4">
                            <p className="font-medium text-[#1a1a2e]">{item.title}</p>
                            {item.year && <span className="text-xs text-[#1a1a2e]/40">{item.year}</span>}
                            {item.description && <p className="text-sm text-[#1a1a2e]/60 mt-1">{item.description}</p>}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Extols - Read Only */}
        <div className="mb-16" data-testid="tribute-extols">
          <h2 className="text-xl font-bold text-[#1a1a2e] mb-6">Words of Appreciation</h2>

          {/* Extol List */}
          <div className="space-y-4">
            {extols.length === 0 ? (
              <p className="text-center text-[#1a1a2e]/30 py-8">No words of appreciation yet</p>
            ) : extols.map(extol => (
              <Card key={extol.id} className="border-[#1a1a2e]/5 bg-white shadow-sm hover:shadow-lg transition-shadow" data-testid={`extol-${extol.id}`}>
                <CardContent className="p-5">
                  <p className="text-[#1a1a2e]/80 mb-3 text-lg italic">"{extol.message}"</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#1a1a2e]/40">- {extol.author_name}</p>
                    <div className="flex gap-2">
                      {[
                        { type: 'heart', emoji: '\u2764\uFE0F', count: extol.reactions?.heart || 0 },
                        { type: 'prayer', emoji: '\uD83D\uDE4F', count: extol.reactions?.prayer || 0 },
                        { type: 'star', emoji: '\u2B50', count: extol.reactions?.star || 0 }
                      ].map(r => (
                        <button key={r.type} onClick={() => handleReaction(extol.id, r.type)}
                          className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#1a1a2e]/5 hover:bg-[#1a1a2e]/10 transition-colors text-sm"
                          data-testid={`react-${r.type}-${extol.id}`}>
                          <span>{r.emoji}</span>
                          {r.count > 0 && <span className="text-xs text-[#1a1a2e]/50">{r.count}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>

      {/* Gallery Modal */}
      {showGalleryModal && selectedImage && (
        <div className="fixed inset-0 z-50 bg-[#1a1a2e]/90 flex items-center justify-center p-4" onClick={() => setShowGalleryModal(false)}>
          <img src={selectedImage.image_url.startsWith('http') ? selectedImage.image_url : `${selectedImage.image_url}`} alt="" className="max-w-full max-h-[90vh] object-contain rounded-2xl" />
        </div>
      )}

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-white border-[#1a1a2e]/10">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a2e]">Report Tribute</DialogTitle>
            <DialogDescription className="text-[#1a1a2e]/50">Help us maintain a safe and respectful platform.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger className="bg-white border-[#1a1a2e]/10 rounded-xl" data-testid="report-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="family_removal">Family requests removal</SelectItem>
                <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                <SelectItem value="harmful_person">Person has caused harm</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)}
              className="bg-[#FDFBF7] border-[#1a1a2e]/5 rounded-xl" rows={3}
              placeholder="Additional details (optional)..." data-testid="report-details" />
            <Button onClick={handleReport} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="submit-report">
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Tribute Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="bg-white border-[#1a1a2e]/10">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a2e]">Send this tribute</DialogTitle>
            <DialogDescription className="text-[#1a1a2e]/50">
              {profile?.profile_type === 'living' 
                ? `Let ${profile?.person_name} know how they've impacted your life.` 
                : `Share this tribute with family or loved ones.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-[#1a1a2e]/70 mb-1 block">Recipient's name</label>
              <Input value={sendData.recipient_name} onChange={(e) => setSendData({ ...sendData, recipient_name: e.target.value })}
                className="border-[#1a1a2e]/10 rounded-xl" placeholder="Their name" data-testid="send-name" />
            </div>
            <div>
              <label className="text-sm text-[#1a1a2e]/70 mb-1 block">Recipient's email</label>
              <Input type="email" value={sendData.recipient_email} onChange={(e) => setSendData({ ...sendData, recipient_email: e.target.value })}
                className="border-[#1a1a2e]/10 rounded-xl" placeholder="their@email.com" data-testid="send-email" />
            </div>
            <div>
              <label className="text-sm text-[#1a1a2e]/70 mb-1 block">Personal message (optional)</label>
              <Textarea value={sendData.personal_message} onChange={(e) => setSendData({ ...sendData, personal_message: e.target.value })}
                className="bg-[#FDFBF7] border-[#1a1a2e]/5 rounded-xl" rows={3}
                placeholder="Add a personal message..." data-testid="send-message" />
            </div>
            <Button onClick={handleSendTribute} disabled={sending}
              className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full shadow-lg" data-testid="send-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Tribute
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-8 bg-[#1a1a2e]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-white/40">&copy; {new Date().getFullYear()} Resilience Community Collective. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
